(function () {
  'use strict';

  var theme = window.PellairTheme || {};
  var heroControllers = new WeakMap();
  var conciergeControllers = new WeakMap();
  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  var mobileViewport = window.matchMedia
    ? window.matchMedia('(max-width: 749px)')
    : { matches: false };
  var reserveFrame = 0;

  function queryWithin(root, selector) {
    var matches = [];
    if (root && root.matches && root.matches(selector)) matches.push(root);
    if (root && root.querySelectorAll) matches = matches.concat(Array.from(root.querySelectorAll(selector)));
    return matches;
  }

  function listenMedia(query, callback) {
    if (!query) return function () {};
    if (query.addEventListener) {
      query.addEventListener('change', callback);
      return function () { query.removeEventListener('change', callback); };
    }
    if (query.addListener) {
      query.addListener(callback);
      return function () { query.removeListener(callback); };
    }
    return function () {};
  }

  function motionIsReduced() {
    return reduceMotion.matches || theme.motionLevel === 'reduced' || theme.motionLevel === 'none';
  }

  function dataSaverIsActive() {
    return Boolean(navigator.connection && navigator.connection.saveData);
  }

  function ambientMotionAllowed() {
    if (theme.designMode || motionIsReduced() || dataSaverIsActive()) return false;
    if (theme.mobileLite !== false && mobileViewport.matches) return false;
    return true;
  }

  function depthMotionAllowed(root) {
    return Boolean(root && root.getAttribute('data-cinematic-enabled') !== 'false' && ambientMotionAllowed());
  }

  function setHeroControlState(root, toggle, paused) {
    root.classList.toggle('is-motion-paused', paused);
    if (!toggle) return;
    var actionLabel = paused
      ? (toggle.getAttribute('data-play-label') || 'Play background motion')
      : (toggle.getAttribute('data-pause-label') || 'Pause background motion');
    toggle.removeAttribute('aria-pressed');
    toggle.setAttribute('title', actionLabel);
  }

  function createHeroController(root) {
    if (heroControllers.has(root)) return;

    var video = root.querySelector('.hero-atelier__video');
    var toggle = root.querySelector('[data-hero-motion-toggle]');
    var strength = Number(root.getAttribute('data-cinematic-strength'));
    var visible = !('IntersectionObserver' in window);
    var assembled = root.classList.contains('is-assembled') || !root.hasAttribute('data-brand-assembly');
    var userPaused = root.getAttribute('data-video-autoplay') === 'false';
    var frame = 0;
    var scrollListening = false;
    var observer = null;
    var destroyed = false;
    var removeReduceListener = function () {};
    var removeMobileListener = function () {};

    if (!Number.isFinite(strength)) strength = 10;
    strength = Math.max(0, Math.min(12, strength));

    function pauseVideo() {
      if (!video || video.paused) return;
      try { video.pause(); } catch (error) {}
    }

    function playVideo() {
      if (!video || !video.paused) return;
      try {
        var playRequest = video.play();
        if (playRequest && playRequest.catch) {
          playRequest.catch(function () {
            userPaused = true;
            setHeroControlState(root, toggle, true);
          });
        }
      } catch (error) {
        setHeroControlState(root, toggle, true);
      }
    }

    function syncVideo() {
      if (!video) return;
      var shouldPlay = visible && !document.hidden && !userPaused && ambientMotionAllowed();
      if (shouldPlay) playVideo();
      else pauseVideo();
      setHeroControlState(root, toggle, video.paused || !shouldPlay);
    }

    function updateDepth() {
      frame = 0;
      if (destroyed || !visible || !assembled || !depthMotionAllowed(root) || strength === 0) {
        root.classList.remove('is-cinematic-active');
        root.style.removeProperty('--hero-media-shift');
        return;
      }

      var bounds = root.getBoundingClientRect();
      var viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      var sceneCenter = bounds.top + bounds.height / 2;
      var travel = Math.max(1, (viewportHeight + bounds.height) / 2);
      var normalized = Math.max(-1, Math.min(1, (viewportHeight / 2 - sceneCenter) / travel));
      var shift = normalized * Math.min(strength, 12);
      root.style.setProperty('--hero-media-shift', shift.toFixed(2) + 'px');
      root.classList.add('is-cinematic-active');
    }

    function scheduleDepth() {
      if (frame || destroyed) return;
      frame = window.requestAnimationFrame(updateDepth);
    }

    function startScrollListener() {
      if (scrollListening || !depthMotionAllowed(root) || strength === 0) return;
      window.addEventListener('scroll', scheduleDepth, { passive: true });
      scrollListening = true;
      scheduleDepth();
    }

    function stopScrollListener() {
      if (!scrollListening) return;
      window.removeEventListener('scroll', scheduleDepth);
      scrollListening = false;
    }

    function reconcile() {
      if (destroyed) return;
      var depthAllowed = depthMotionAllowed(root) && strength > 0;
      var ambientAllowed = ambientMotionAllowed();
      root.classList.toggle('is-cinematic-static', !depthAllowed);
      root.classList.toggle('is-ambient-motion-disabled', !ambientAllowed);
      if (video && toggle) video.controls = !ambientAllowed;
      if (visible && depthAllowed) startScrollListener();
      else {
        stopScrollListener();
        root.classList.remove('is-cinematic-active');
        root.style.removeProperty('--hero-media-shift');
      }
      syncVideo();
      scheduleDepth();
    }

    function onVisibilityChange() {
      syncVideo();
    }

    function onAssemblyComplete() {
      assembled = true;
      scheduleDepth();
    }

    function onToggleClick() {
      if (!video) return;
      userPaused = !video.paused;
      if (userPaused) pauseVideo();
      else playVideo();
      window.requestAnimationFrame(function () {
        setHeroControlState(root, toggle, video.paused || userPaused);
      });
    }

    function onVideoPlay() {
      setHeroControlState(root, toggle, false);
    }

    function onVideoPause() {
      setHeroControlState(root, toggle, true);
    }

    if (toggle && video) toggle.addEventListener('click', onToggleClick);
    if (video) {
      video.addEventListener('play', onVideoPlay);
      video.addEventListener('pause', onVideoPause);
    }
    root.classList.add('is-cinematic-enhanced');
    root.addEventListener('pellair:assembly-complete', onAssemblyComplete);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('resize', reconcile, { passive: true });
    removeReduceListener = listenMedia(reduceMotion, reconcile);
    removeMobileListener = listenMedia(mobileViewport, reconcile);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target !== root) return;
          visible = entry.isIntersecting;
          reconcile();
        });
      }, { rootMargin: '120px 0px', threshold: 0 });
      observer.observe(root);
    }

    setHeroControlState(root, toggle, Boolean(video && video.paused));
    reconcile();

    heroControllers.set(root, {
      destroy: function () {
        destroyed = true;
        stopScrollListener();
        if (frame) window.cancelAnimationFrame(frame);
        if (observer) observer.disconnect();
        if (toggle && video) toggle.removeEventListener('click', onToggleClick);
        if (video) {
          video.removeEventListener('play', onVideoPlay);
          video.removeEventListener('pause', onVideoPause);
          video.controls = true;
        }
        root.removeEventListener('pellair:assembly-complete', onAssemblyComplete);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('resize', reconcile);
        removeReduceListener();
        removeMobileListener();
        root.style.removeProperty('--hero-media-shift');
        root.classList.remove('is-cinematic-active', 'is-cinematic-static', 'is-ambient-motion-disabled', 'is-cinematic-enhanced');
        pauseVideo();
        heroControllers.delete(root);
      }
    });
  }

  function isElementVisible(element) {
    if (!element || !element.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    if (!element.getClientRects || element.getClientRects().length === 0) return false;
    var style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) !== 0;
  }

  function createConciergeController(link) {
    if (conciergeControllers.has(link)) return;

    var footer = document.querySelector('[data-footer]');
    var sticky = document.querySelector('[data-mobile-sticky-purchase]');
    var stickyStatus = sticky && sticky.querySelector('[data-mobile-sticky-status]');
    var footerNearby = false;
    var observer = null;
    var stickyObserver = null;
    var bodyObserver = null;
    var consentObserver = null;
    var consentElement = null;
    var originalTabIndex = link.getAttribute('tabindex');
    var overlayClasses = [
      'mobile-nav-open',
      'predictive-search-open',
      'finder-select-open',
      'collection-filter-open',
      'cart-drawer-is-open'
    ];

    function findConsentElement() {
      return document.querySelector('[data-cookie-consent], #shopify-pc__banner, .shopify-pc__banner__dialog');
    }

    function bindConsentObserver() {
      var nextConsent = findConsentElement();
      if (nextConsent === consentElement) return;
      if (consentObserver) consentObserver.disconnect();
      consentObserver = null;
      consentElement = nextConsent;
      if (consentElement && 'MutationObserver' in window) {
        consentObserver = new MutationObserver(sync);
        consentObserver.observe(consentElement, {
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
        });
      }
    }

    function hasBlockingOverlay() {
      var body = document.body;
      if (!body) return false;
      if (overlayClasses.some(function (className) { return body.classList.contains(className); })) return true;
      return isElementVisible(consentElement);
    }

    function stickyObstructs() {
      if (!sticky) return false;
      return sticky.classList.contains('is-menu-open') || Boolean(stickyStatus && !stickyStatus.hidden);
    }

    function setSuppressed(suppressed) {
      link.classList.toggle('is-concierge-suppressed', suppressed);
      if (suppressed) {
        if (document.activeElement === link && link.blur) link.blur();
        link.setAttribute('aria-hidden', 'true');
        link.setAttribute('inert', '');
        link.setAttribute('tabindex', '-1');
      } else {
        link.removeAttribute('aria-hidden');
        link.removeAttribute('inert');
        if (originalTabIndex === null) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', originalTabIndex);
      }
    }

    function sync() {
      bindConsentObserver();
      var obstructed = stickyObstructs();
      if (document.body) document.body.classList.toggle('whatsapp-sticky-obstructed', obstructed);
      setSuppressed(footerNearby || obstructed || hasBlockingOverlay() || document.documentElement.classList.contains('intro-pending'));
    }

    if (footer && link.getAttribute('data-hide-near-footer') !== 'false' && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target !== footer) return;
          footerNearby = entry.isIntersecting;
          link.classList.toggle('is-near-footer', footerNearby);
          sync();
        });
      }, { rootMargin: '0px 0px 96px 0px', threshold: 0 });
      observer.observe(footer);
    }

    if (sticky && 'MutationObserver' in window) {
      stickyObserver = new MutationObserver(sync);
      stickyObserver.observe(sticky, { attributes: true, subtree: true, attributeFilter: ['class', 'hidden', 'aria-hidden'] });
    }
    if (document.body && 'MutationObserver' in window) {
      bodyObserver = new MutationObserver(sync);
      bodyObserver.observe(document.body, { attributes: true, childList: true, attributeFilter: ['class'] });
    }

    document.addEventListener('pellair:intro-complete', sync);
    sync();

    conciergeControllers.set(link, {
      destroy: function () {
        if (observer) observer.disconnect();
        if (stickyObserver) stickyObserver.disconnect();
        if (bodyObserver) bodyObserver.disconnect();
        if (consentObserver) consentObserver.disconnect();
        document.removeEventListener('pellair:intro-complete', sync);
        if (document.body) document.body.classList.remove('whatsapp-sticky-obstructed');
        setSuppressed(false);
        conciergeControllers.delete(link);
      }
    });
  }

  function measureSystemBottomSpace() {
    reserveFrame = 0;
    var reserve = 0;
    var selectors = [
      '#preview-bar-iframe',
      'iframe[src*="preview_bar"]',
      '[data-shopify-preview-bar]'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(function (element) {
      if (!isElementVisible(element)) return;
      var bounds = element.getBoundingClientRect();
      var style = window.getComputedStyle(element);
      if ((style.position === 'fixed' || style.position === 'sticky') && bounds.bottom >= window.innerHeight - 2) {
        reserve = Math.max(reserve, Math.ceil(bounds.height));
      }
    });
    document.documentElement.style.setProperty('--pellair-system-bottom-space', reserve + 'px');
  }

  function scheduleSystemBottomSpace() {
    if (reserveFrame) return;
    reserveFrame = window.requestAnimationFrame(measureSystemBottomSpace);
  }

  function initWithin(root) {
    queryWithin(root || document, '[data-cinematic-hero]').forEach(createHeroController);
    queryWithin(root || document, '[data-whatsapp-contact]').forEach(createConciergeController);
    scheduleSystemBottomSpace();
  }

  function destroyWithin(root) {
    queryWithin(root, '[data-cinematic-hero]').forEach(function (element) {
      var controller = heroControllers.get(element);
      if (controller) controller.destroy();
    });
    queryWithin(root, '[data-whatsapp-contact]').forEach(function (element) {
      var controller = conciergeControllers.get(element);
      if (controller) controller.destroy();
    });
  }

  document.addEventListener('shopify:section:load', function (event) { initWithin(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyWithin(event.target); });
  window.addEventListener('resize', scheduleSystemBottomSpace, { passive: true });
  window.addEventListener('load', scheduleSystemBottomSpace, { once: true });
  window.setTimeout(scheduleSystemBottomSpace, 800);
  window.setTimeout(scheduleSystemBottomSpace, 1800);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initWithin(document); }, { once: true });
  } else {
    initWithin(document);
  }
})();
